import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";

interface ExplanationCardProps {
  text: string | null;
  isLoading: boolean;
  /** Number of evidence items in the result, used to validate inline [N] refs. */
  evidenceCount: number;
}

const SkeletonGroup = styled("div")({});

const ExplanationParagraph = styled(Typography)({
  lineHeight: 1.7,
  marginTop: 0,
  marginBottom: "12px",
  "&:last-child": { marginBottom: 0 },
});

const CitationSup = styled("sup")(({ theme }) => ({
  fontSize: "0.7em",
  fontWeight: 600,
  color: theme.palette.primary.main,
  marginLeft: "1px",
  marginRight: "1px",
}));

const CitationAnchor = styled("a")({
  color: "inherit",
  textDecoration: "none",
  "&:hover": { textDecoration: "underline" },
});

const markdownComponents = {
  p: ({ children }: { children: React.ReactNode }) => (
    <ExplanationParagraph variant="body2" component="p" color="text.primary">
      {children}
    </ExplanationParagraph>
  ),
  sup: ({ children }: { children: React.ReactNode }) => <CitationSup>{children}</CitationSup>,
  a: ({ href, children }: { href?: string; children: React.ReactNode }) => (
    <CitationAnchor href={href}>{children}</CitationAnchor>
  ),
};

/** Renders the explanation as markdown prose. The LLM uses **bold** for key
 *  elements and [N] tokens for inline citations; citations are preprocessed to
 *  HTML <sup> tags before handing off to react-markdown + rehype-raw.
 *  Renders nothing on silent failure (text=null, isLoading=false). */
export function ExplanationCard({ text, isLoading, evidenceCount }: ExplanationCardProps) {
  if (!text && !isLoading) return null;

  if (isLoading) {
    return (
      <SkeletonGroup>
        <Skeleton width="100%" />
        <Skeleton width="100%" />
        <Skeleton width="92%" />
        <Skeleton width="78%" />
      </SkeletonGroup>
    );
  }

  return (
    <ReactMarkdown rehypePlugins={[rehypeRaw]} components={markdownComponents}>
      {preprocessCitations(text!, evidenceCount)}
    </ReactMarkdown>
  );
}

/** Converts valid `[N]` citation tokens to linked `<sup>` HTML so rehype-raw
 *  renders them as anchor links to the corresponding source card. Out-of-range
 *  ids are left as plain text. */
function preprocessCitations(text: string, evidenceCount: number): string {
  return text.replace(/\[(\d+)\]/g, (match, n) => {
    const idx = parseInt(n, 10);
    if (idx < 1 || idx > evidenceCount) return match;
    return `<sup><a href="#source-${idx}">[${idx}]</a></sup>`;
  });
}
